/**
 * clients.service.ts — Layer 3: Clients Domain
 */

import { getRequest, deleteRequest, postRequest, putRequest } from './http.service';
import { notifyClientChanged } from "../hooks/useClientChangeListener";
import {
  URL_CLIENTS,
  URL_CLIENTS_MENTIONABLE,
  URL_CLIENTS_MENTIONABLE_SEARCH,
  URL_CLIENTS_BY_ORG,
  URL_CLIENT_BY_ID,
  URL_MEETING_CLIENTS,
  URL_MEETING_CLIENT_BY_ID,
  URL_ACTIVITY_CLIENT_SUMMARY,
  URL_CLIENT_ADVISERS,
  URL_CLIENT_ADVISER,
  URL_ABR,
  URL_ABR_SEARCH,
} from './url.service';

export interface ClientMentionCandidate {
  id: string;
  name: string;
  createdAt?: string;
  lastActivityAt?: string;
  contactEmail: string | null;
}

/**
 * For a name shared by more than one client in the SAME result list, append
 * the linked contact's email to disambiguate — "John Smith (john@x.com)" vs
 * plain "John Smith" for anyone whose name is already unique in the list, so
 * most options stay uncluttered.
 */
export function labelClientsForPicker(clients: ClientMentionCandidate[]): { id: string; label: string }[] {
  const nameCounts = new Map<string, number>();
  clients.forEach((c) => nameCounts.set(c.name, (nameCounts.get(c.name) || 0) + 1));
  return clients.map((c) => ({
    id: c.id,
    label: (nameCounts.get(c.name) || 0) > 1 && c.contactEmail ? `${c.name} (${c.contactEmail})` : c.name,
  }));
}

export interface AbrDetails {
  abn: string;
  abnStatus: string;
  entityName: string;
  entityTypeCode: string;
  entityTypeName: string;
  gst: string;
  addressState: string;
  addressPostcode: string;
  businessNames: string[];
}

export interface AbrNameResult {
  abn: string;
  abnStatus: string;
  name: string;
  nameType: string;
  state: string;
  postcode: string;
}

/** Get all clients for an org */
export const fetchClients = (orgId: string) =>
  getRequest(URL_CLIENTS_BY_ORG(orgId));

/** Get all clients (no org filter) */
export const fetchAllClients = () =>
  getRequest(URL_CLIENTS);

/** Get a single client by ID */
export const fetchClientById = (clientId: string) =>
  getRequest(URL_CLIENT_BY_ID(clientId));

/**
 * Org clients + primary contact email, for the chat @mention picker and the
 * workflow builder's client picker. With `q`, searches server-side (capped at
 * `limit`, default 50 either way) instead of the caller fetching everything
 * and filtering locally.
 */
export const fetchClientsForMentions = (q?: string, limit?: number): Promise<ClientMentionCandidate[]> =>
  getRequest(q || limit ? URL_CLIENTS_MENTIONABLE_SEARCH(q, limit) : URL_CLIENTS_MENTIONABLE);

/** Create a new client */
export const createClient = async (payload: Record<string, unknown>) => {
  const res: any = await postRequest(URL_CLIENTS, payload);
  // Announced here rather than at each call site: several screens create clients
  // (Home, Clients, Contacts, the calendar wizard, contact import) and any open
  // view derived from "which clients exist" has to re-derive. Chiefly the Home
  // calendar's "not a Prospo client" banner, whose classification is server-side
  // and so cannot know a client was just created.
  if (!res?.error) notifyClientChanged();
  return res;
};

/** Delete a client */
export const deleteClient = async (clientId: string) => {
  const response = await deleteRequest(URL_CLIENT_BY_ID(clientId));
  if (!(response as any)?.error) notifyClientChanged();
  return response;
};

/** Update a client's details */
export const updateClient = async (clientId: string, payload: Record<string, unknown>) => {
  const response = await putRequest(URL_CLIENT_BY_ID(clientId), payload);
  if (!(response as any)?.error) notifyClientChanged();
  return response;
};

/** Get the AI-generated activity summary for a client (dashboard widget) */
export const fetchClientActivitySummary = (clientId: string) =>
  getRequest(URL_ACTIVITY_CLIENT_SUMMARY(clientId));

/** Get the advisers assigned to a client */
export const fetchClientAdvisers = (clientId: string) =>
  getRequest(URL_CLIENT_ADVISERS(clientId));

/** Assign an adviser to a client */
export const addClientAdviser = (clientId: string, userId: string) =>
  postRequest(URL_CLIENT_ADVISERS(clientId), { userId });

/** Remove an adviser from a client */
export const removeClientAdviser = (clientId: string, userId: string) =>
  deleteRequest(URL_CLIENT_ADVISER(clientId, userId));

/** Look up an ABN on the ABR (returns null if unconfigured / not found). */
export const lookupAbr = async (
  abn: string,
): Promise<{ details: AbrDetails | null; configured: boolean }> => {
  const res = await getRequest<{ success: boolean; data: AbrDetails | null; configured: boolean }>(
    URL_ABR(abn.replace(/\D/g, '')),
  );
  return { details: res.data ?? null, configured: !!res.configured };
};

/** Search the ABR by business name for candidate ABNs. */
export const searchAbr = async (
  name: string,
): Promise<{ results: AbrNameResult[]; configured: boolean }> => {
  const res = await getRequest<{ success: boolean; data: AbrNameResult[]; configured: boolean }>(
    URL_ABR_SEARCH(name),
  );
  return { results: res.data ?? [], configured: !!res.configured };
};

// ---------------------------------------------------------------------------
// Legacy "meeting_clients" entity (older client-relationship CRUD screen)
// ---------------------------------------------------------------------------

export const fetchMeetingClients = () =>
  getRequest(URL_MEETING_CLIENTS);

export const createMeetingClient = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_CLIENTS, payload);

export const updateMeetingClient = (clientId: string, payload: Record<string, unknown>) =>
  putRequest(URL_MEETING_CLIENT_BY_ID(clientId), payload);

export const deleteMeetingClient = (clientId: string) =>
  deleteRequest(URL_MEETING_CLIENT_BY_ID(clientId));
